import { Action, createReducer, Store } from "ngrx-actions";
import { cartInitialState, CartState } from "./state";
import * as CartActions from "./cart-actions";
import { PosQuote } from "../../models";

/**
 * As the redux documentation, it internally calls the reducer methods twice, so all
 *  Reduce Actions must be pure,(
 *    i.e If an reducer action is triggered with same set of parameters multiple times,
 *    it must return the same value
 *  ).
 *  FYI : Use of spread Operation will workaround this, but it will do shallow copy of the nested object.
 *  so we end up in using raw objects. where can't access the getter or other internal methods.
 */
@Store({ ...cartInitialState })
export class CartReducers {
  constructor() {}

  @Action(CartActions.ActionAddUser)
  loadUser(state: CartState, action: CartActions.ActionAddUser) {
    const newQuote: PosQuote = state.quote;
    newQuote.user = action.user;

    return {
      ...state,
      quote: newQuote,
    };
  }

  /**
   *
   * @param {CartState} state
   * @param {ActionUpdateQuoteSuccess} action
   */
  @Action(CartActions.ActionUpdateQuoteSuccess)
  updateQuoteSuccess(
    state: CartState,
    action: CartActions.ActionUpdateQuoteSuccess
  ) {
    return {
      ...state,
      error: false,
      loading: false,
      quote: action.quote,
    };
  }

  /**
   * set the loading as true and trigger the addProduct effect
   * @param {CartState} state
   * @param {ActionCartAddProduct} action
   */
  @Action(CartActions.ActionCartAddProduct)
  addProduct(state: CartState, action: CartActions.ActionCartAddProduct) {
    return {
      ...state,
      loading: true,
      error: false,
    };
  }

  @Action(CartActions.ActionCartAddBarcode)
  addBarcode(state: CartState, action: CartActions.ActionCartAddBarcode) {
    const newQuote: PosQuote = state.quote;
    const isSameBarcode = state.quote.cartBarcodes.some(
      (barcodes) => barcodes == action.barcode
    );
    //to avoid duplicate entry
    if (!isSameBarcode) {
      newQuote.cartBarcodes.push(action.barcode);
    }
    return {
      ...state,
      quote: newQuote,
    };
  }

  @Action(CartActions.ActionCartRemoveProduct)
  removeProduct(state: CartState, action: CartActions.ActionCartRemoveProduct) {
    return {
      ...state,
      loading: true,
      error: false,
    };
  }

  @Action(CartActions.ActionQuoteApplyReward)
  applyRewardPoints(
    state: CartState,
    action: CartActions.ActionQuoteApplyReward
  ) {
    return {
      ...state,
      loading: true,
    };
  }

  @Action(CartActions.ActionQuoteRemoveReward)
  removeRewardPoints(
    state: CartState,
    action: CartActions.ActionQuoteRemoveReward
  ) {
    return {
      ...state,
      loading: true,
    };
  }

  @Action(CartActions.ActionQuoteSetPayment)
  setPaymentMode(state: CartState, action: CartActions.ActionQuoteSetPayment) {
    const newQuote: PosQuote = state.quote;
    newQuote.paymentMethod = action.paymentMode;
    return {
      ...state,
      quote: newQuote,
    };
  }

  @Action(CartActions.ActionQuoteClearPayment)
  clearPaymentMode(
    state: CartState,
    action: CartActions.ActionQuoteClearPayment
  ) {
    const newQuote: PosQuote = state.quote;
    newQuote.paymentMethod = null;
    return {
      ...state,
      quote: newQuote,
    };
  }

  @Action(CartActions.ActionCartCardDetail)
  setCardDetail(state: CartState, action: CartActions.ActionCartCardDetail) {
    const newQuote: PosQuote = state.quote;
    newQuote.transactionId = action.transactionId;
    newQuote.cardNumber = action.cardNumber;
    return {
      ...state,
      quote: newQuote,
    };
  }

  @Action(CartActions.ActionCartTenderCash)
  setTenderCash(state: CartState, action: CartActions.ActionCartTenderCash) {
    const newQuote: PosQuote = state.quote;
    newQuote.tenderCash = action.tenderCash;
    newQuote.changeCash = action.changeCash;
    return {
      ...state,
      quote: newQuote,
    };
  }

  @Action(CartActions.ActionQuoteSetOrderId)
  setOrderId(state: CartState, action: CartActions.ActionQuoteSetOrderId) {
    const newQuote = state.quote;
    newQuote.orderId = action.orderId;
    return {
      ...state,
      quote: newQuote,
    };
  }

  @Action(CartActions.ActionQuoteFromLocal)
  updateQuoteFromLocal(state: CartState, action: CartActions.ActionQuoteFromLocal) {
    const newQuote = action.quote;
    return {
      ...state,
      quote: newQuote,
    };
  }

  /**
   * order placing in process.
   * loading true
   * @param state
   */
  @Action(CartActions.ActionCreateOrder)
  createOrder(state: CartState) {
    return {
      ...state,
      loading: true,
    };
  }

  /**
   * Order placing in process.
   * loading true
   * @param state
   */
  @Action(CartActions.ActionCreateInstantSyncOrder)
  createComboOrder(state: CartState) {
    return {
      ...state,
      loading: true,
    };
  }

  /**
   * order placed.
   * loading false
   * @param state
   */
  @Action(CartActions.ActionOrderPlaced)
  orderPlaced(state: CartState, action: CartActions.ActionOrderPlaced) {
    return {
      ...state,
      loading: false,
    };
  }

  @Action(CartActions.ActionInstantSyncOrderSuccess)
  comboOrderPlaced(
    state: CartState,
    action: CartActions.ActionInstantSyncOrderSuccess
  ) {
    return {
      ...state,
      loading: false,
      saleOrder: action.saleOrder,
    };
  }

  @Action(CartActions.ActionCartLoadFailed)
  loadFailed(state: CartState, action: CartActions.ActionCartLoadFailed) {
    return {
      ...state,
      loading: false,
      error: true,
      errorMsg: action.payload,
    };
  }

  @Action(CartActions.ActionInstantSyncFailed)
  ActionInstantSyncFailed(
    state: CartState,
    action: CartActions.ActionInstantSyncFailed
  ) {
    return {
      ...state,
      loading: false,
      error: true,
    };
  }

  @Action(CartActions.ActionClearCart)
  clearCart(state: CartState) {
    return {
      ...state,
      quote: new PosQuote(),
    };
  }

  @Action(CartActions.ActionQuoteApplyCoupon)
  applyCoupon(state: CartState, action: CartActions.ActionQuoteApplyCoupon) {
    return {
      ...state,
      loading: true,
      couponCodeError: false,
    };
  }

  @Action(CartActions.ActionInvalidCouponApplied)
  invalidCouponApplied(state: CartState) {
    return {
      ...state,
      loading: false,
      couponCodeError: true,
    };
  }

  /**
   * adjust the discount amount on every product
   * updating item prices based on the discount
   * @param state
   * @param action
   */
  @Action(CartActions.ActionQuoteCouponApplied)
  couponApplied(
    state: CartState,
    action: CartActions.ActionQuoteCouponApplied
  ) {
    return {
      ...state,
      error: false,
      loading: true,
      rewardsEarned: action.validateCouponCode.rewardsEarned,
    };
  }

  /**
   * removing the discount and updating item prices
   * @param state
   * @param action
   */
  @Action(CartActions.ActionRemoveDiscount)
  removeCouponApplied(
    state: CartState,
    action: CartActions.ActionRemoveDiscount
  ) {
    return {
      ...state,
      loading: true,
      rewardsEarned: null,
    };
  }

  @Action(CartActions.ActionUploadPinelabsBill)
  uploadPinelabsBill(state: CartState, action: CartActions.ActionUploadPinelabsBill,refId) {
    return {
      ...state,
      loading: true,
      error: false,
    };
  }

  /**
   * after pinelabs txn uploaded
   * @param state
   * @param action
   */
  @Action(CartActions.ActionUploadPinelabsBillDone)
  uploadPinelabsBillDone(
    state: CartState,
    action: CartActions.ActionUploadPinelabsBillDone
  ) {
    state.quote.plutusTransactionReferenceID = action.plutusTransactionReferenceID;
    return {
      ...state,
      loading: false,
      error: false,
    };
  }

  /**
   * removing the discount and updating item prices
   * @param state
   * @param action
   */
   @Action(CartActions.ActionUploadPinelabsBillFailed)
   uploadPinelabsBillFailed(
     state: CartState,
   ) {
     return {
       ...state,
       loading: false,
       error: true,
     };
   }

  /**
   * cancelling plutos pending transaction
   * @param state
   * @param action
   */
  @Action(CartActions.ActionCancelPlutosTransaction)
  cancelPlutosTransaction(state: CartState, action: CartActions.ActionCancelPlutosTransaction) {
    return {
      ...state,
      loading: true,
      error: false,
    };
  }

  /**
   * cancelling plutos pending transaction
   * @param state
   * @param action
   */
  @Action(CartActions.ActionCancelPlutosTransactionFailed)
  ActionCancelPlutosTransactionFailed(state: CartState, action: CartActions.ActionCancelPlutosTransactionFailed) {
    return {
      ...state,
      loading: false,
      error: true,
    };
  }


  /**
   * cancelling plutos pending transaction
   * @param state
   * @param action
   */
  @Action(CartActions.ActionPlutosTransactionCancelled)
  plutosTransactionCancelled(
    state: CartState,
  ) {
    state.quote.plutusTransactionReferenceID = null;
    return {
      ...state,
      loading: false,
      error: false,
    };
  }

   /**
   * checking plutos transaction status
   * @param state
   * @param action
   */
    @Action(CartActions.ActionCheckPlutosTransactionStatus)
    checkPlutosTransactionStatus(state: CartState, action: CartActions.ActionCheckPlutosTransactionStatus) {
      return {
        ...state,
        loading: true,
        error: false,
      };
    }

   /**
   * checking plutos transaction status
   * @param state
   * @param action
   */
    @Action(CartActions.ActionPlutosTransactionStatusChecked)
    plutosTransactionStatusChecked(state: CartState, action: CartActions.ActionPlutosTransactionStatusChecked) {
      state.quote.isPlutusTransactionApproved = action.isPlutusTransactionApproved;
      return {
        ...state,
        loading: false,
        error: false,
      };
    }

   /**
   * checking plutos transaction status
   * @param state
   * @param action
   */
    @Action(CartActions.ActionPlutosTransactionStatusFailed)
    plutosTransactionStatusFailed(state: CartState, action: CartActions.ActionPlutosTransactionStatusFailed) {
      state.quote.isPlutusTransactionApproved = action.isPlutusTransactionApproved;
      return {
        ...state,
        loading: false,
        error: true,
      };
    }

   /**
   * update plutos reference id
   * @param state
   * @param action
   */
    @Action(CartActions.ActionUpdatePlutosReferenceId)
    ActionUpdatePlutosReferenceId(state: CartState, action: CartActions.ActionUpdatePlutosReferenceId) {
      state.quote.plutusTransactionReferenceID = action.plutusTransactionReferenceID;
      return {
        ...state,
        loading: false,
        error: false,
      };
    }

}

export const cartReducer = createReducer(CartReducers);
